import { OfflineButton } from '@/components/studio/OfflineNotice';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { motion, type MotionProps } from 'framer-motion';
import { Check, X, Clock, Film, Sparkles, Mic, Eye, Zap, Captions, AudioLines, Clapperboard, Music2, Hand, PackageCheck, LayoutGrid, ShieldCheck, ListChecks } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useStoryAvailability } from '@/lib/story-availability';
import { trackEvent } from '@/lib/analytics';
import { requestWorkflowStart, type WorkflowId } from '@/components/landing/director-studio/studioBus';
import type { ToolId } from '@/components/landing/ToolCompass';
import { ADS_FALLBACK_CONFIG, fetchAdsConfig, type AdsConfig } from '@/lib/ads';
import { hasFinePointer, isSmallScreen, prefersReducedMotion } from '@/lib/motion';
import AvatarEntryLink from '@/components/facelock/AvatarEntryLink';

// WhyCorridoSection — the TOP-of-page sales pitch, now COMPASS-AWARE: the same
// layout and rules transform per active tool (corrido / TikTok story /
// commercials & ads / rap coming-soon). CTAs start the tool's workflow directly in the console via the
// studio bus (requestWorkflowStart) — with the compass set, nobody re-picks
// their tool inside the chat.
//
// ALL COPY IS TRANSLATED: every visible string resolves through `t()` against
// locales/{en,es}.json, so flipping the language toggle re-renders this whole
// pitch — table cells and image alt text included.
//
// PROOF MEDIA (2026-09-03). Every still and clip below is our own engine's
// output, pulled from real storyboard runs under tmp/story_work/corridos_v2/.
// Since 2026-09-10 the four tap-to-cycle CLIP cards sit beside the headline,
// in the grid where four still images used to be — the owner wanted moving
// proof next to "Your song. Your face.", not in a section further down. The
// still files stay in /promo because the Asset Library still uses them.
//
// Genre names on the cards are proper names and NEVER translate: en.json and
// es.json carry the identical string for every corrido.styles.<genre> title,
// and genreTitles.test.ts fails the build if they ever drift apart. The
// previous media set was retired for cause:
// a Chalino Sánchez mural photograph, an image carrying a Gemini watermark, and
// the "Narco Cultura" film poster — none of it ours. Everything here was
// screened at full resolution (and the clips frame-by-frame) for third-party
// watermarks, library photos leaking through, brand marks and artist likeness.
//
// The genre chip is the genre the SCENE sells. Two clips (trap, narco) come from
// runs whose style parameter was bélico/tumbado — the scene content is what a
// customer judges, but do not re-badge these as "rendered with style=trap".
// Exported so the welcome stop's corrido card quotes the same intro price.
export const INTRO_PRICE = 99;

type Genre = 'traditional' | 'romantico' | 'sad-sierreno' | 'tumbado' | 'trap' | 'narco' | 'belico';

// The tilted, staggered collage the four stills used, kept for the clips that
// replaced them so the pitch's look does not change — only what moves in it.
const CLIP_TILT: Array<{ rot: number; y: number }> = [
  { rot: -4, y: 18 },
  { rot: 2.5, y: 0 },
  { rot: -2, y: 26 },
  { rot: 4, y: 8 },
];

// Clips — animated shots from finished renders, re-encoded to 360x624 for the
// page (70-115 KB each). THREE variations per genre; tapping a card cycles to
// the next one so a visitor can see more than a single shot without leaving the
// page. The poster frame stops the black flash before autoplay kicks in.
//
// Provenance — every variation is our own engine's output, from
// run_df04992a18 (bélico) and run_e4563f1ddf (chill/tumbado b-roll):
//   belico      df04992a18 31 (Guadalajara sign, first on purpose), 12, 15
//   traditional df04992a18 19, then e4563f1ddf 18 (jaripeo rider), 05
//   trap        e4563f1ddf 25, 06, then df04992a18 33
//   narco       df04992a18 02, then e4563f1ddf 21, df04992a18 18
// Replaced 2026-09-11 after the owner's review of the live page: df04992a18
// 05 (morphing gold medallions), 08 (bricks), 17 (man on marble stairs), and
// 24 + 35 (the same vested-men-at-night subject on two cards). e4563f1ddf 24
// was screened and rejected: its licence plate is gibberish at full size.
// Shots 22, 27 and 30 were pulled during screening — 22 and 27 carry gibberish
// label/apparel text and 30 has a real-world sports-cap monogram, all three of
// which the promo-media rules exclude. Screen replacements at FULL RES: none of
// that is legible in a thumbnail.
//
// Honest caveat, unchanged from the single-clip version: only bélico and
// chill/tumbado runs have ever been animated, so the traditional/trap/narco
// chips name the SCENE each clip sells, not the style parameter it was rendered
// with. Style-tag-exact clips for those genres need renders that do not exist.
type ClipVariant = { src: string; poster: string };
const CORRIDO_CLIPS: Array<{ genre: Genre; variants: ClipVariant[] }> = (
  ['belico', 'traditional', 'trap', 'narco'] as const
).map((genre) => ({
  genre: genre as Genre,
  variants: [
    { src: `/promo/corrido_${genre}.mp4`, poster: `/promo/poster_${genre}.jpg` },
    { src: `/promo/corrido_${genre}_2.mp4`, poster: `/promo/poster_${genre}_2.jpg` },
    { src: `/promo/corrido_${genre}_3.mp4`, poster: `/promo/poster_${genre}_3.jpg` },
  ],
}));
// loopEnd (2026-09-24): the render's captions end with "SKY." at ~6.5s and the
// last second is plain, blocky blue sky under a "captions included" badge. The
// pitch loops the whole captioned sentence and stops there. The poster is the
// same frame the welcome card shows; under reduced motion it is all you see.
const STORY_CLIPS: Array<{ src: string; poster: string; loopEnd: number }> = [{ src: '/promo/story_clip_1.mp4', poster: '/promo/poster_story_1.jpg', loopEnd: 6.5 }];

const CORRIDO_BENEFITS = [
  { icon: Mic, key: 'pitch.corrido.benefit1' },
  { icon: Eye, key: 'pitch.corrido.benefit2' },
  { icon: Zap, key: 'pitch.corrido.benefit3' },
];

const STORY_BENEFITS = [
  { icon: AudioLines, key: 'pitch.story.benefit1' },
  { icon: Clapperboard, key: 'pitch.story.benefit2' },
  { icon: Captions, key: 'pitch.story.benefit3' },
];

type Row = { stem: string; crewOk: boolean | null; aiOk: boolean | null; oursOk: boolean | null };

// Cell copy comes from `pitch.rows.<stem>{Label,Crew,Ai,Ours}`; only the
// check/cross marks stay in code.
const ROWS: Row[] = [
  { stem: 'onCamera', crewOk: true, aiOk: false, oursOk: true },
  { stem: 'finished', crewOk: true, aiOk: false, oursOk: true },
  { stem: 'genre', crewOk: null, aiOk: false, oursOk: true },
  { stem: 'approve', crewOk: null, aiOk: false, oursOk: true },
  { stem: 'turnaround', crewOk: false, aiOk: null, oursOk: true },
  { stem: 'cost', crewOk: false, aiOk: null, oursOk: true },
];

const WAIT_POINTS = [
  { icon: Sparkles, stem: 'wait1' },
  { icon: Film, stem: 'wait2' },
  { icon: Clock, stem: 'wait3' },
];

const STORY_POINTS = [
  { icon: AudioLines, stem: 'point1' },
  { icon: Eye, stem: 'point2' },
  { icon: Zap, stem: 'point3' },
];

// Commercials & Ads. NO proof media here on purpose: the tool has no shipped
// renders yet, and a stock clip or a "real render" badge would be a lie. The
// right-hand column is a CSS-drawn ad frame — an illustration, labelled as one.
const ADS_BENEFITS = [
  { icon: PackageCheck, key: 'pitch.ads.benefit1' },
  { icon: Mic, key: 'pitch.ads.benefit2' },
  { icon: LayoutGrid, key: 'pitch.ads.benefit3' },
];

const ADS_POINTS = [
  { icon: ShieldCheck, stem: 'point1' },
  { icon: ListChecks, stem: 'point2' },
  { icon: Zap, stem: 'point3' },
];

// Tier rows read their credits straight off the /config ladder (§9); the
// bundled fallback only covers the signed-out visitor who cannot call it.
const ADS_TIERS: Array<{ id: string; stem: string; credits: (c: AdsConfig) => number; from?: boolean }> = [
  { id: 'spot_15', stem: 'spot15', credits: (c) => c.credits.spot['15'] },
  { id: 'spot_30', stem: 'spot30', credits: (c) => c.credits.spot['30'] },
  { id: 'spot_60', stem: 'spot60', credits: (c) => c.credits.spot['60'] },
  { id: 'stills_8', stem: 'stills8', credits: (c) => c.credits.still * 8 },
  { id: 'package', stem: 'package', credits: (c) => Math.min(...Object.values(c.credits.package)), from: true },
];

function Mark({ ok }: { ok: boolean | null }) {
  if (ok === true) return <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />;
  if (ok === false) return <X className="mt-0.5 h-4 w-4 shrink-0 text-red-400/80" />;
  return <span className="mt-0.5 inline-block h-4 w-4 shrink-0 text-center text-muted-foreground">–</span>;
}

function StartCta({ workflowId, label, source, className = '' }: { workflowId: WorkflowId; label: string; source: string; className?: string }) {
  return (
    <a
      href="#director-console"
      onClick={() => { trackEvent('corrido_promo_cta_clicked', { source }); requestWorkflowStart(workflowId); }}
      className={`inline-flex min-h-[44px] items-center justify-center rounded-lg border border-white/10 bg-[linear-gradient(135deg,hsl(var(--primary)),hsl(var(--accent))_55%,rgb(92,104,118))] px-7 py-3 text-[15px] font-bold tracking-wide sm:text-sm text-primary-foreground shadow-[0_12px_30px_rgba(0,0,0,0.4)] transition hover:opacity-95 active:scale-[0.98] ${className}`}
    >
      {label}
    </a>
  );
}

// Phone layout notes (2026-09-24, formatting only — same copy, same keys):
// the pitch buttons go full width below sm so two CTAs of different widths do
// not sit ragged on two rows; the eyebrow pill loosens its tracking and may
// wrap as a rounded block (the Spanish one wrapped mid-pill with a dangling
// "·"); body copy is text-base (15px on a phone, the root steps down to 15px
// below 480px) instead of text-sm's 13px. The CTAs too (2026-09-25): 13px
// gradient pills read small beside the 15px price line under them.
const PITCH_ACTIONS = 'mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center';
const PITCH_BUTTON = 'w-full sm:w-auto';
const EYEBROW = 'mb-3 inline-flex items-center gap-2 rounded-2xl border border-primary/30 bg-primary/10 px-4 py-1.5 text-[11px] font-bold uppercase leading-snug tracking-[0.14em] text-primary text-balance [overflow-wrap:anywhere] max-[399px]:tracking-[0.1em] sm:rounded-full sm:tracking-[0.22em]';
const POINT_BODY = 'mt-2 text-base leading-relaxed text-muted-foreground sm:text-sm';
// Headings on a phone (2026-09-24): at text-3xl the ads title broke as "Your
// product. Your / words. / A finished / commercial." and the Spanish corrido
// title stranded "verdad." on a line of its own. The two title parts are
// separate blocks (a <br> stops text-wrap:balance from evening out the second
// part) and a step smaller below 400px: 24px keeps "A finished commercial."
// and "Un comercial terminado." on one line at 360. Tablet and desktop sizes
// are unchanged. One more step below 360 (2026-09-25) so the Spanish
// "Un comercial terminado." holds one line at 320 too.
const PITCH_H2 = 'text-balance text-[1.6rem] font-bold leading-[1.15] max-[359px]:text-[1.45rem] min-[400px]:text-3xl sm:text-4xl lg:text-[2.6rem]';
const PITCH_H3 = 'text-balance text-center text-2xl font-bold sm:text-3xl';

// Card chips were 9px and truncated ("CORRIDO TRADICION…", "MADE WITH CORRI…")
// on a phone. The genre name is 11px and may take two lines; the provenance
// badge shows on each card from sm up, and once under the grid on a phone,
// where a ~150px card has no room for it beside the variation dots.
const CHIP = 'rounded bg-black/70 px-1.5 py-0.5 font-bold uppercase leading-tight text-white/90';

// Pitch reveals (2026-09-25): on a phone or any touch screen every block is
// simply there. A busy phone (WebKit most of all) held whole blocks at
// framer's opacity-0 start for a frame or more, so the screen under the h1
// and under the comparison table's CTA sat blank. The fade-up stays where a
// mouse is in use. `initial` is read once, on mount.
const revealFrom = (from: MotionProps['initial']): MotionProps['initial'] =>
  (isSmallScreen() || !hasFinePointer() ? false : from);

/** Decode a pitch clip only while a quarter of it is on screen (2026-09-25):
 * the corrido stop had all four clips playing at once on a phone, and kept
 * decoding them after the visitor had scrolled on to the console. `autoPlay`
 * still starts it; this pauses it off screen and resumes it on the way back.
 * Nothing happens where IntersectionObserver is missing (tests), or under
 * prefers-reduced-motion, where the clips stay on their poster frame. */
function usePlayWhileVisible(ref: RefObject<HTMLVideoElement | null>, src: string) {
  useEffect(() => {
    const video = ref.current;
    if (!video || typeof IntersectionObserver === 'undefined' || prefersReducedMotion()) return undefined;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
        void video.play()?.catch(() => { /* low-power mode or no autoplay: the poster stays */ });
      } else {
        video.pause();
      }
    }, { threshold: [0, 0.25] });
    io.observe(video);
    return () => io.disconnect();
  }, [ref, src]);
}

// Keep a separator with the words before it, so a wrapped line never starts
// with "·" or "—" (2026-09-25). Display only: the locale strings are the owner's.
const glueBefore = (text: string, mark: string) => text.split(` ${mark} `).join(`\u00a0${mark} `);

// The eyebrow chips break at their " · " only (2026-09-25): word by word the
// Spanish ones split inside the tool's name ("VIDEOS MUSICALES DE / CORRIDO ·
// ..."). Non-breaking spaces hold each clause together, the "·" stays with
// the clause before it, and overflow-wrap on the chip is the safety net for a
// clause wider than the screen. Display only, like glueBefore.
const keepClauses = (text: string) => glueBefore(text.split(' · ').map((clause) => clause.replace(/ /g, '\u00a0')).join(' · '), '·');

/** One genre's clip. Tapping (or clicking, or Enter/Space on a keyboard) swaps
 * in the next variation of the SAME genre and wraps around at the end. It is a
 * real <button> so a thumb, a mouse and a screen reader all get the same thing;
 * the dots show how many variations there are and which one is playing.
 *
 * Only the active variation is mounted, so a card still costs one video.
 *
 * A clip that fails to load degrades to a styled placeholder — never a blank or
 * broken video box — and reports itself once, so a missing asset shows up in
 * analytics instead of staying silent. Failure is tracked per variation, so
 * tapping on still reaches the other two, which may load fine. */
function ClipCard({ variants, label, badge, genre }: { variants: ClipVariant[]; label: string; badge: string; genre: Genre }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<Record<number, true>>({});
  const clip = variants[index];
  const videoRef = useRef<HTMLVideoElement>(null);
  usePlayWhileVisible(videoRef, failed[index] ? '' : clip.src);
  // Reduced motion: no autoplay, the poster frame stands in (read once).
  const [reduced] = useState(prefersReducedMotion);

  const next = () => {
    const to = (index + 1) % variants.length;
    setIndex(to);
    trackEvent('corrido_promo_clip_tapped', { genre, from: index, to, variants: variants.length });
  };

  return (
    <button
      type="button"
      onClick={next}
      data-testid={`promo-clip-${genre}`}
      data-variant={index}
      aria-label={t('pitch.corrido.tapForNext', { label })}
      className="group relative block w-full cursor-pointer overflow-hidden rounded-xl border border-white/15 shadow-[0_20px_45px_rgba(0,0,0,0.5)] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {failed[index] ? (
        <div className="flex aspect-[9/16] w-full items-center justify-center bg-[linear-gradient(160deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02))]">
          <Film className="h-8 w-8 text-muted-foreground" />
        </div>
      ) : (
        <video
          // Keying on src forces a fresh element per variation, so the new clip
          // starts from its own first frame instead of inheriting the old one's
          // playhead and showing a torn frame mid-swap.
          key={clip.src}
          ref={videoRef}
          src={clip.src}
          poster={clip.poster}
          muted
          loop
          autoPlay={!reduced}
          playsInline
          preload="metadata"
          className="aspect-[9/16] w-full object-cover"
          onError={() => {
            trackEvent('corrido_promo_clip_error', { genre, variant: index, src: clip.src });
            setFailed((f) => ({ ...f, [index]: true }));
          }}
        />
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
      {/* Some genre names are long, so the label gets the room and truncates
          rather than colliding with the tap affordance opposite it. */}
      {/* translate="no": our own locale files already keep genre names identical
          in both languages, but browser page translation would still rewrite
          "Corrido tradicional" to "Traditional corrido" on a visitor's screen. */}
      <span
        translate="no"
        className={`notranslate absolute left-1.5 top-1.5 line-clamp-2 max-w-[calc(100%_-_0.75rem)] text-left text-[11px] tracking-wide sm:max-w-[calc(100%_-_2.6rem)] ${CHIP}`}
      >
        {label}
      </span>
      <span className={`absolute bottom-1.5 left-1.5 hidden max-w-[calc(100%_-_3.4rem)] text-left text-[11px] tracking-normal sm:line-clamp-2 ${CHIP}`}>{badge}</span>

      {/* Which variation is playing, and that there are more. */}
      <span className="absolute bottom-1.5 right-1.5 flex items-center gap-1">
        {variants.map((v, i) => (
          <span
            key={v.src}
            className={`block h-1.5 rounded-full transition-all ${i === index ? 'w-3 bg-white' : 'w-1.5 bg-white/40'}`}
          />
        ))}
      </span>

      {/* The affordance, ALWAYS visible. It used to hide on desktop until
          hover, and the owner looked straight at the cards without realising
          they could be tapped. Icon only — the word did not fit beside the
          longer genre names; the dots opposite say there is more than one clip,
          and the button's aria-label spells it out for screen readers. On a
          phone it sits bottom-left, where the badge is not shown, so the
          genre name gets the card's full width ("NARCOCORRIDO" was cut at
          320px). */}
      <span
        title={t('pitch.corrido.tapBadge')}
        className="pointer-events-none absolute bottom-1.5 left-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white/90 sm:bottom-auto sm:left-auto sm:right-1.5 sm:top-1.5"
      >
        <Hand className="h-3 w-3" />
      </span>
    </button>
  );
}

function CorridoPitch() {
  const { t } = useTranslation();
  return (
    <>
      <motion.div
        initial={revealFrom({ opacity: 0, y: 24 })}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55 }}
        className="mx-auto mt-2 grid max-w-6xl items-center gap-10 lg:grid-cols-2"
      >
        <div className="text-left">
          <div className={EYEBROW}>
            {keepClauses(t('pitch.corrido.eyebrow'))}
          </div>
          <h2 className={PITCH_H2}>
            <span className="block">{t('pitch.corrido.titleLine1')}</span>
            <span className="gradient-text">{t('pitch.corrido.titleGradient')}</span>
          </h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t('pitch.corrido.leadStart')} <span className="font-semibold text-foreground">{t('pitch.corrido.leadBold')}</span>
            {t('pitch.corrido.leadEnd')}
          </p>
          <ul className="mt-5 space-y-3">
            {CORRIDO_BENEFITS.map((b) => (
              <li key={b.key} className="flex items-start gap-3 text-base text-foreground">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15">
                  <b.icon className="h-3.5 w-3.5 text-primary" />
                </span>
                {t(b.key)}
              </li>
            ))}
          </ul>
          <div className={PITCH_ACTIONS}>
            <StartCta workflowId="corrido" label={t('pitch.corrido.cta')} source="promo_pitch" className={PITCH_BUTTON} />
            <a
              href="#compare"
              onClick={() => trackEvent('corrido_promo_cta_clicked', { source: 'promo_compare_link' })}
              className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-white/15 bg-white/5 px-6 py-3 text-[15px] font-semibold text-foreground transition hover:bg-white/10 sm:text-sm"
            >
              {t('pitch.corrido.compareLink')}
            </a>
            <AvatarEntryLink source="corrido" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/5 px-6 py-3 text-[15px] font-semibold text-foreground transition hover:bg-white/10 sm:text-sm" />
          </div>
          {/* Below sm the two halves stack instead of wrapping around the "|"
              (Spanish broke the bold price across lines), and neither figure
              is split. */}
          <div className="mt-4 text-[15px] leading-snug text-muted-foreground sm:text-sm">
            <span className="block sm:inline">
              {t('pitch.corrido.introPriceLabel')} <span className="whitespace-nowrap font-bold text-foreground">{t('pitch.corrido.introPriceValue', { price: INTRO_PRICE })}</span>
            </span>
            <span className="mx-2 hidden text-white/20 sm:inline">|</span>
            <span className="mt-1 block sm:mt-0 sm:inline">
              {t('pitch.corrido.crewCostLabel')} <span className="whitespace-nowrap line-through decoration-red-400/60">{t('pitch.corrido.crewCostValue')}</span>
            </span>
          </div>
        </div>

        {/* STUDIO-FINISHED SAMPLES beside the headline — four genres, three
            tap-through variations each. They are July b-roll from the earlier
            engine with no artist shot, so the badge says "sample", not "real
            render", until an owner-approved render from the current engine
            exists (audit 2026-09-23, should-fix 6). 9:16 cards run taller than
            the 3:4 stills they replaced, so the grid is capped narrower to keep
            the column's height close to what it was. */}
        {/* px-3 below sm: the ±4° tilt pushed the outer card corners to within
            7-10px of a 360px screen's edge. */}
        <div className="relative mx-auto grid w-full max-w-[22rem] grid-cols-2 gap-3 px-3 sm:max-w-md sm:gap-4 sm:px-0 lg:max-w-[26rem] lg:gap-5">
          {CORRIDO_CLIPS.map((c, i) => {
            const tilt = CLIP_TILT[i % CLIP_TILT.length];
            return (
              <motion.div
                key={c.genre}
                initial={revealFrom({ opacity: 0, y: 26, rotate: 0 })}
                animate={{ opacity: 1, y: tilt.y, rotate: tilt.rot }}
                transition={{ duration: 0.55, delay: 0.2 + i * 0.1 }}
                whileHover={{ y: tilt.y - 8, rotate: 0, scale: 1.03, zIndex: 10 }}
                className="relative"
              >
                <ClipCard
                  genre={c.genre}
                  variants={c.variants}
                  label={t(`corrido.styles.${c.genre}`)}
                  badge={t('pitch.corrido.realRenderBadge')}
                />
              </motion.div>
            );
          })}
        </div>
        {/* The per-card provenance badge, said once on a phone (see CHIP). */}
        <p className="text-center text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground sm:hidden">
          {t('pitch.corrido.realRenderBadge')}
        </p>
      </motion.div>

      {/* HOW IT COMPARES */}
      <motion.div
        id="compare"
        initial={revealFrom({ opacity: 0, y: 24 })}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.5 }}
        className="mx-auto mt-20 max-w-5xl scroll-mt-24"
      >
        <h3 className={PITCH_H3}>
          {t('pitch.corrido.compareTitleStart')} <span className="gradient-text">{t('pitch.corrido.compareTitleGradient')}</span>
        </h3>
        <p className="mx-auto mt-3 max-w-2xl text-center text-base text-muted-foreground">
          {t('pitch.corrido.compareDesc')}
        </p>
        {/* PHONES (2026-09-24): the 640px table sat in a sideways-scrolling
            box, so a 360px screen showed the row labels and "Hire a crew"
            only — the Corrido Studio column, the point of the table, was
            off-screen with nothing saying to swipe. Below sm the same ROWS
            and the same copy stack: one block per row, the three answers
            under it, ours last and tinted like its table column. */}
        <div className="mt-8 divide-y divide-white/10 overflow-hidden rounded-2xl border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] shadow-[0_20px_45px_rgba(0,0,0,0.34)] sm:hidden">
          {ROWS.map((row) => (
            // ≤359px (2026-09-25): a narrower label column and tighter rows —
            // at 320 the answers had ~150px and ran to 3-4 lines each.
            <div key={row.stem} className="px-3 py-4 max-[359px]:px-2 max-[359px]:py-3">
              <div className="px-2 text-[15px] font-semibold leading-snug text-foreground">{t(`pitch.rows.${row.stem}Label`)}</div>
              <dl className="mt-2.5 space-y-1">
                {([
                  { col: 'colCrew', cell: 'Crew', ok: row.crewOk, ours: false },
                  { col: 'colAi', cell: 'Ai', ok: row.aiOk, ours: false },
                  { col: 'colOurs', cell: 'Ours', ok: row.oursOk, ours: true },
                ] as const).map((c) => (
                  <div
                    key={c.col}
                    className={`grid grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-3 rounded-xl px-2 py-2 max-[359px]:grid-cols-[5.25rem_minmax(0,1fr)] max-[359px]:gap-2 max-[359px]:py-1.5 ${c.ours ? 'bg-primary/[0.07] ring-1 ring-inset ring-primary/20' : ''}`}
                  >
                    <dt className={`pt-0.5 text-[11px] font-semibold uppercase leading-snug tracking-[0.05em] ${c.ours ? 'text-primary' : 'text-muted-foreground'}`}>
                      {t(`pitch.corrido.${c.col}`)}
                    </dt>
                    <dd className={`flex gap-2 text-[14px] leading-snug ${c.ours ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                      <Mark ok={c.ok} />
                      <span>{t(`pitch.rows.${row.stem}${c.cell}`, { price: INTRO_PRICE })}</span>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
        <div className="mt-8 hidden overflow-x-auto rounded-2xl border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] shadow-[0_20px_45px_rgba(0,0,0,0.34)] sm:block">
          <table className="w-full min-w-[36rem] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-muted-foreground">
                <th className="p-4 font-semibold" />
                <th className="p-4 font-semibold">{t('pitch.corrido.colCrew')}</th>
                <th className="p-4 font-semibold">{t('pitch.corrido.colAi')}</th>
                <th className="p-4 font-bold text-primary">{t('pitch.corrido.colOurs')}</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.stem} className="border-b border-white/5 align-top last:border-0">
                  <td className="p-4 font-medium text-foreground">{t(`pitch.rows.${row.stem}Label`)}</td>
                  <td className="p-4 text-muted-foreground"><span className="flex gap-2"><Mark ok={row.crewOk} />{t(`pitch.rows.${row.stem}Crew`)}</span></td>
                  <td className="p-4 text-muted-foreground"><span className="flex gap-2"><Mark ok={row.aiOk} />{t(`pitch.rows.${row.stem}Ai`)}</span></td>
                  <td className="bg-primary/[0.07] p-4 font-medium text-foreground"><span className="flex gap-2"><Mark ok={row.oursOk} />{t(`pitch.rows.${row.stem}Ours`, { price: INTRO_PRICE })}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-center text-xs text-muted-foreground">
          {t('pitch.corrido.compareFootnote')}
        </p>
        <div className="mt-6 text-center">
          <StartCta workflowId="corrido" label={t('pitch.corrido.cta')} source="promo_compare_table" />
        </div>
      </motion.div>

      {/* WHY THE WAIT IS WORTH IT */}
      <motion.div
        initial={revealFrom({ opacity: 0, y: 24 })}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="mx-auto mt-16 max-w-5xl"
      >
        <h3 className={PITCH_H3}>{t('pitch.corrido.waitTitle')}</h3>
        <p className="mx-auto mb-8 mt-3 max-w-2xl text-center text-base text-muted-foreground">
          {t('pitch.corrido.waitDesc')}
        </p>
        <div className="grid gap-4 text-left sm:grid-cols-3">
          {WAIT_POINTS.map((point) => (
            <div key={point.stem} className="rounded-2xl border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] p-6 shadow-[0_18px_34px_rgba(0,0,0,0.28)]">
              <point.icon className="mb-3 h-6 w-6 text-primary" />
              <div className="text-base font-semibold text-foreground">{t(`pitch.corrido.${point.stem}Title`)}</div>
              <p className={POINT_BODY}>{t(`pitch.corrido.${point.stem}Body`)}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </>
  );
}

/** A muted autoplay loop that jumps back to 0 at `loopEnd` instead of playing
 * the file to its end. Same approach as ToolChooser's CardClip: checked on
 * every decoded frame where the browser can (Chrome, Safari 15.4+), with
 * `timeupdate` (about 4 a second) as the fallback, so it cuts a little early
 * rather than a frame late. The `loop` attribute stays for anything else. */
function LoopVideo({ src, poster, loopEnd, className }: { src: string; poster: string; loopEnd: number; className?: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  usePlayWhileVisible(ref, src);
  // Reduced motion: no autoplay, the poster frame stands in (read once).
  const [reduced] = useState(prefersReducedMotion);
  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;
    if (typeof video.requestVideoFrameCallback === 'function') {
      let handle = 0;
      const onFrame: VideoFrameRequestCallback = (_now, meta) => {
        if (meta.mediaTime >= loopEnd) video.currentTime = 0;
        handle = video.requestVideoFrameCallback(onFrame);
      };
      handle = video.requestVideoFrameCallback(onFrame);
      return () => video.cancelVideoFrameCallback?.(handle);
    }
    const onTime = () => {
      if (video.currentTime >= loopEnd - 0.2) video.currentTime = 0;
    };
    video.addEventListener('timeupdate', onTime);
    return () => video.removeEventListener('timeupdate', onTime);
  }, [loopEnd]);
  return <video ref={ref} src={src} poster={poster} muted loop autoPlay={!reduced} playsInline preload="metadata" className={className} />;
}

function StoryPitch() {
  const { t } = useTranslation();
  const storyAvailable = useStoryAvailability();
  return (
    <>
      <motion.div
        initial={revealFrom({ opacity: 0, y: 24 })}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55 }}
        className="mx-auto mt-2 grid max-w-6xl items-center gap-10 lg:grid-cols-2"
      >
        <div className="text-left">
          <div className={EYEBROW}>
            {keepClauses(t('pitch.story.eyebrow'))}
          </div>
          <h2 className={PITCH_H2}>
            <span className="block">{t('pitch.story.titleLine1')}</span>
            <span className="gradient-text">{t('pitch.story.titleGradient')}</span>
          </h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t('pitch.story.leadStart')}
            <span className="font-semibold text-foreground"> {t('pitch.story.leadBold')}</span>
            {t('pitch.story.leadEnd')}
          </p>
          <ul className="mt-5 space-y-3">
            {STORY_BENEFITS.map((b) => (
              <li key={b.key} className="flex items-start gap-3 text-base text-foreground">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15">
                  <b.icon className="h-3.5 w-3.5 text-primary" />
                </span>
                {t(b.key)}
              </li>
            ))}
          </ul>
          {storyAvailable ? (
            <div className={PITCH_ACTIONS}>
              <StartCta workflowId="tiktok_story" label={t('pitch.story.cta')} source="story_pitch" className={PITCH_BUTTON} />
            </div>
          ) : (
            // Temporarily offline (owner 2026-10-16): the greyed button, back by itself.
            <div data-testid="story-pitch-coming-soon" className={PITCH_ACTIONS}>
              <OfflineButton className="sm:max-w-sm" />
            </div>
          )}
        </div>

        {/* Real output — captions burned in, straight from the story engine. */}
        <div className="mx-auto flex w-full max-w-md items-center justify-center gap-4">
          {STORY_CLIPS.map((clip) => (
            <motion.div key={clip.src} initial={revealFrom({ opacity: 0, y: 26 })} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.2 }}>
              <div className="relative overflow-hidden rounded-2xl border border-white/15 shadow-[0_24px_50px_rgba(0,0,0,0.55)]">
                <LoopVideo src={clip.src} poster={clip.poster} loopEnd={clip.loopEnd} className="aspect-[9/16] w-56 object-cover sm:w-64" />
                {/* Same 8px inset on both sides; wraps after the "·" when it
                    must, in two even lines. */}
                <span className="absolute bottom-2 left-2 max-w-[calc(100%-1rem)] text-balance rounded bg-black/70 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white/90">
                  {glueBefore(t('pitch.story.clipBadge'), '·')}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={revealFrom({ opacity: 0, y: 24 })}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.5 }}
        className="mx-auto mt-16 grid max-w-5xl gap-4 text-left sm:grid-cols-3"
      >
        {STORY_POINTS.map((p) => (
          <div key={p.stem} className="rounded-2xl border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] p-6 shadow-[0_18px_34px_rgba(0,0,0,0.28)]">
            <p.icon className="mb-3 h-6 w-6 text-primary" />
            <div className="text-base font-semibold text-foreground">{t(`pitch.story.${p.stem}Title`)}</div>
            <p className={POINT_BODY}>{t(`pitch.story.${p.stem}Body`)}</p>
          </div>
        ))}
      </motion.div>
    </>
  );
}

/** A CSS-only 9:16 ad frame: product slot, headline bar, price chip, CTA.
 *  Deliberately abstract — it illustrates the LAYOUT the tool produces, and is
 *  captioned as an illustration so it can never be mistaken for output. */
function AdFrameIllustration() {
  const { t } = useTranslation();
  return (
    // 12.5rem on a phone (2026-09-25): at 15rem the wireframe stood ~446px
    // tall, nearly a whole screen of placeholder.
    <div data-testid="ads-frame" className="relative mx-auto w-full max-w-[12.5rem] sm:max-w-[16rem]">
      <div
        aria-hidden="true"
        className="relative aspect-[9/16] w-full overflow-hidden rounded-[1.6rem] border border-white/15 shadow-[0_24px_50px_rgba(0,0,0,0.55)]"
        style={{ background: 'linear-gradient(170deg, hsl(var(--primary) / 0.22), rgba(255,255,255,0.03) 45%, hsl(var(--accent) / 0.18))' }}
      >
        {/* headline bar */}
        <div className="absolute left-4 right-4 top-6 space-y-2">
          <div className="h-3 w-3/4 rounded-full bg-white/80" />
          <div className="h-2 w-1/2 rounded-full bg-white/40" />
        </div>
        {/* Spacing (2026-09-24): the price chip sat on the product slot's
            corner and the caption track touched the CTA. Slot centred at 45%,
            chip at 70%, track at 4.5rem — each keeps clear air. */}
        {/* the product slot — a hard-edged frame, because the product is pasted, not painted */}
        <div className="absolute left-1/2 top-[45%] flex h-[38%] w-[58%] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-xl border-2 border-dashed border-white/70 bg-black/25">
          <PackageCheck className="h-8 w-8 text-white/85" />
        </div>
        {/* price chip */}
        <div className="absolute right-4 top-[70%] rounded-full bg-primary px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider text-primary-foreground">
          {t('pitch.ads.frame.price')}
        </div>
        {/* CTA */}
        <div className="absolute bottom-6 left-4 right-4 rounded-lg bg-white/90 py-2 text-center text-[11px] font-extrabold uppercase tracking-widest text-black">
          {t('pitch.ads.frame.cta')}
        </div>
        {/* caption track */}
        <div className="absolute bottom-[4.5rem] left-6 right-6 flex justify-center gap-1">
          <span className="h-1.5 w-6 rounded-full bg-accent" />
          <span className="h-1.5 w-10 rounded-full bg-white/60" />
          <span className="h-1.5 w-4 rounded-full bg-white/40" />
        </div>
      </div>
      <div className="-mx-6 mt-3 text-balance text-center text-[11px] uppercase tracking-[0.2em] text-muted-foreground sm:mx-0">{glueBefore(t('pitch.ads.frame.caption'), '—')}</div>
    </div>
  );
}

function AdsPitch() {
  const { t } = useTranslation();
  const [config, setConfig] = useState<AdsConfig>(ADS_FALLBACK_CONFIG);
  const [fromServer, setFromServer] = useState(false);
  useEffect(() => {
    let alive = true;
    void fetchAdsConfig().then((r) => { if (alive) { setConfig(r.config); setFromServer(r.fromServer); } });
    return () => { alive = false; };
  }, []);
  return (
    <>
      <motion.div
        initial={revealFrom({ opacity: 0, y: 24 })}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55 }}
        className="mx-auto mt-2 grid max-w-6xl items-center gap-10 lg:grid-cols-2"
        data-testid="ads-pitch"
      >
        <div className="text-left">
          <div className={EYEBROW}>
            {keepClauses(t('pitch.ads.eyebrow'))}
          </div>
          <h2 className={PITCH_H2}>
            <span className="block">{t('pitch.ads.titleLine1')}</span>
            <span className="gradient-text">{t('pitch.ads.titleGradient')}</span>
          </h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t('pitch.ads.leadStart')}
            <span className="font-semibold text-foreground"> {t('pitch.ads.leadBold')}</span>
            {t('pitch.ads.leadEnd')}
          </p>
          <ul className="mt-5 space-y-3">
            {ADS_BENEFITS.map((b) => (
              <li key={b.key} className="flex items-start gap-3 text-base text-foreground">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15">
                  <b.icon className="h-3.5 w-3.5 text-primary" />
                </span>
                {t(b.key)}
              </li>
            ))}
          </ul>
          <div className={PITCH_ACTIONS}>
            <StartCta workflowId="ads" label={t('pitch.ads.cta')} source="ads_pitch" className={PITCH_BUTTON} />
            <AvatarEntryLink source="ads" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/5 px-6 py-3 text-[15px] font-semibold text-foreground transition hover:bg-white/10 sm:text-sm" />
          </div>
        </div>

        <AdFrameIllustration />
      </motion.div>

      {/* TIERS — the exact credit ladder, read from the service. */}
      <motion.div
        initial={revealFrom({ opacity: 0, y: 24 })}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.5 }}
        className="mx-auto mt-16 max-w-5xl"
      >
        <h3 className={PITCH_H3}>{t('pitch.ads.tiersTitle')}</h3>
        <p className="mx-auto mt-3 max-w-2xl text-center text-base text-muted-foreground">{t('pitch.ads.tiersDesc')}</p>
        {/* Below sm the ladder is ONE card of divided rows, price on the right
            (2026-09-24): five full-width ~110px cards took a screen and a half
            of a phone. From sm up it is the card grid it always was. */}
        <div
          className="mt-8 grid gap-3 max-sm:gap-0 max-sm:divide-y max-sm:divide-white/10 max-sm:overflow-hidden max-sm:rounded-2xl max-sm:border max-sm:border-white/10 max-sm:bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] max-sm:shadow-[0_18px_34px_rgba(0,0,0,0.28)] sm:grid-cols-2 lg:grid-cols-5"
          data-testid="ads-tiers" data-source={fromServer ? 'server' : 'fallback'}
        >
          {/* Phone row (2026-09-25): name and credits share the first line
              and the description runs the full row under them; beside the
              credits it was squeezed into five short lines in Spanish. The
              "×" stays with its number ("× 8" was left alone on a line). */}
          {ADS_TIERS.map((tier) => (
            <div key={tier.id} data-testid={`ads-tier-${tier.id}`} className="text-left max-sm:grid max-sm:grid-cols-[minmax(0,1fr)_auto] max-sm:items-baseline max-sm:gap-x-4 max-sm:px-4 max-sm:py-3.5 sm:rounded-2xl sm:border sm:border-white/10 sm:bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] sm:p-5 sm:shadow-[0_18px_34px_rgba(0,0,0,0.28)]">
              <div className="min-w-0 max-sm:contents">
                <div className="text-balance text-[15px] font-semibold text-foreground max-sm:col-start-1 max-sm:row-start-1 sm:text-sm">{t(`pitch.ads.tiers.${tier.stem}`).split(' × ').join('\u00a0×\u00a0')}</div>
                <div className="mt-1 text-[14px] leading-snug text-muted-foreground max-sm:col-span-2 max-sm:row-start-2 sm:text-xs">{t(`pitch.ads.tiers.${tier.stem}Desc`)}</div>
              </div>
              <div className="shrink-0 whitespace-nowrap text-right text-base font-extrabold text-primary max-sm:col-start-2 max-sm:row-start-1 sm:mt-3 sm:text-left sm:text-lg" data-testid={`ads-tier-${tier.id}-credits`}>
                {tier.from ? t('pitch.ads.creditsFrom', { count: tier.credits(config) }) : t('pitch.ads.credits', { count: tier.credits(config) })}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-center text-xs text-muted-foreground">{t('pitch.ads.tiersFootnote')}</p>
      </motion.div>

      {/* HOW IT WORKS */}
      <motion.div
        initial={revealFrom({ opacity: 0, y: 24 })}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="mx-auto mt-16 grid max-w-5xl gap-4 text-left sm:grid-cols-3"
      >
        {ADS_POINTS.map((p) => (
          <div key={p.stem} className="rounded-2xl border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] p-6 shadow-[0_18px_34px_rgba(0,0,0,0.28)]">
            <p.icon className="mb-3 h-6 w-6 text-primary" />
            <div className="text-base font-semibold text-foreground">{t(`pitch.ads.${p.stem}Title`)}</div>
            <p className={POINT_BODY}>{t(`pitch.ads.${p.stem}Body`)}</p>
          </div>
        ))}
      </motion.div>
    </>
  );
}

function RapTeaser() {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={revealFrom({ opacity: 0, y: 24 })}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55 }}
      className="mx-auto mt-6 max-w-3xl text-center"
    >
      {/* px-5 below sm: 32px sides left the Spanish title 228px at 320 and
          it broke into four lines. */}
      <div className="rounded-3xl border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] px-5 py-12 shadow-[0_24px_50px_rgba(0,0,0,0.4)] sm:px-8 sm:py-16">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-primary">
          <Music2 className="h-3.5 w-3.5" /> {t('pitch.rap.comingSoon')}
        </div>
        <h2 className="text-balance text-[1.6rem] font-bold leading-[1.15] max-[359px]:text-[1.45rem] min-[400px]:text-3xl sm:text-4xl">
          {t('pitch.rap.titleStart')} <span className="gradient-text">{t('pitch.rap.titleGradient')}</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          {glueBefore(t('pitch.rap.body'), '—')}
        </p>
        {/* 15px on a phone like the body above it (2026-09-25; was 13px). */}
        <p className="mx-auto mt-8 max-w-xl text-balance text-[15px] text-muted-foreground sm:text-sm">
          {t('pitch.rap.footnote')}
        </p>
      </div>
    </motion.div>
  );
}

export function WhyCorridoSection({ tool = 'corrido' }: { tool?: ToolId }) {
  return (
    <div id="why-corrido" className="relative">
      {tool === 'corrido' && <CorridoPitch />}
      {tool === 'story' && <StoryPitch />}
      {tool === 'ads' && <AdsPitch />}
      {tool === 'rap' && <RapTeaser />}
    </div>
  );
}
